import { Controller } from "@hotwired/stimulus"

// Keeps the visible Pause control (a minutes input, or a dropdown of preset
// minutes) and the hidden time_break field in step.
//
// BOTH directions matter. The employee's edit flows visible -> hidden, and an
// outside write flows back: break_rule_controller raises time_break to the
// statutory minimum, and it writes the HIDDEN field because that is the one that
// submits. Without the return leg the employee is left looking at a Pause of 0
// next to a note saying it was raised to 00:30 — the correction is invisible,
// which is the one thing that controller exists to prevent.
export default class extends Controller {
  static targets = ["minutes", "hidden"]

  connect() {
    this._applyHidden = () => this.readHidden()
    this._focusIn = () => {
      this._focused = true
    }
    this._focusOut = () => {
      this._focused = false
      this.readHidden()
    }
    this.hiddenTarget.addEventListener("change", this._applyHidden)
    this.minutesTarget.addEventListener("focus", this._focusIn)
    this.minutesTarget.addEventListener("blur", this._focusOut)
    this.sync()
  }

  disconnect() {
    this.hiddenTarget.removeEventListener("change", this._applyHidden)
    this.minutesTarget.removeEventListener("focus", this._focusIn)
    this.minutesTarget.removeEventListener("blur", this._focusOut)
  }

  sync() {
    const totalMinutes = parseInt(this.minutesTarget.value) || 0
    const hours = Math.floor(totalMinutes / 60)
    const mins = totalMinutes % 60
    this.hiddenTarget.value = String(hours).padStart(2, "0") + ":" + String(mins).padStart(2, "0")
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // An echo is told from a genuine outside write by VALUE, never by a flag around
  // our own dispatch. break_rule_controller listens on the form and only reacts to
  // fields NAMED time_break — the visible control here has no name, so the only
  // event that ever reaches it is the one sync() dispatches, and the raise it
  // makes in response lands back here still INSIDE that dispatch. A flag bracketing
  // the round trip would swallow exactly the correction this leg exists to deliver.
  // Comparing values cannot: an echo carries what the control already shows, a
  // raise carries something else.
  readHidden() {
    const parts = String(this.hiddenTarget.value || "").split(":")
    if (parts.length < 2) return

    const minutes = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10)
    if (Number.isNaN(minutes) || minutes === this.shownMinutes) return
    // Not while the employee is typing. A raise fires on the keystroke that first
    // parses, and rewriting the field under the caret turns a typed "30" into
    // "600". The blur applies whatever the hidden field settled on.
    if (this._focused) return

    this.showMinutes(minutes)
  }

  get shownMinutes() {
    const shown = parseInt(this.minutesTarget.value, 10)
    return Number.isNaN(shown) ? null : shown
  }

  // The dropdown variant's control is a SlimSelect-backed <select>, whose own UI
  // does not follow a bare `select.value =` — its controller exposes setSelected
  // for exactly this.
  showMinutes(minutes) {
    if (this.minutesTarget.tagName !== "SELECT") {
      this.minutesTarget.value = String(minutes)
      return
    }
    // A value the dropdown cannot offer must not be forced into it: the select
    // would land on some other option, #sync would write THAT back to the hidden
    // field, and whoever raised it would raise it again — a loop. The hidden
    // field still carries the right value into the submit.
    if (![...this.minutesTarget.options].some((option) => option.value === String(minutes))) return

    const select = this.application.getControllerForElementAndIdentifier(
      this.minutesTarget,
      "select",
    )
    // No select controller yet means SlimSelect has not mounted, and until it does
    // the native <select> is the control the employee can see — so writing it is
    // right, not a fallback that hides the value.
    if (select) select.setSelected(String(minutes))
    else this.minutesTarget.value = String(minutes)
  }
}
